import React from 'react';
import {
  TableRow,
  TableCell,
  IconButton,
  Typography,
  Box,
  Checkbox,
  Tooltip,
} from '@mui/material';
import {
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowRight as CollapseIcon,
  ContentCopy,
} from '@mui/icons-material';
import { Campaign, AdStatus } from '../../../types/ads.types';
import { AdRow } from './AdRow';
import { AdSetSeparator } from './AdSetSeparator';
import { formatCurrency, formatPercentage, formatNumber, formatDate } from '../../../utils/formatters';
import {
  calculateRPM,
  calculateRPC,
  calculateCPA,
  calculateCPC,
  calculatePageCTR,
  calculateCoverage,
  calculateROAS,
  getEarliestDate,
  getLatestDate,
} from '../../../utils/adsMetrics';
import TrendChart from '../../../components/TrendChart/TrendChart';

interface CampaignRowProps {
  campaign: Campaign;
  expanded: boolean;
  selectedAds: Set<string>;
  showGraphs: boolean;
  breakdownMode: 'daily' | 'hourly';
  updatingAdsetId?: string | null;
  updatingAdId?: string | null;
  onToggle: () => void;
  onSelectAd: (adId: string) => void;
  onUpdateStatus: (adId: string, status: AdStatus) => void;
  onUpdateBudget: (adsetId: string, budget: number) => void;
  onDuplicate?: (adId: string) => void;
  expandedAds?: Set<string>;
  onToggleAdExpansion?: (adId: string) => void;
  expandedDates?: Set<string>;
  onToggleDateExpansion?: (adId: string, date: string) => void;
}

/**
 * CampaignRow - Displays a campaign row with expandable ad list
 *
 * Features:
 * - Expand/collapse to show/hide ads
 * - Shows aggregated campaign metrics
 * - Groups ads by adset with separators
 * - Bulk selection of all ads in campaign
 */
export const CampaignRow: React.FC<CampaignRowProps> = ({
  campaign,
  expanded,
  selectedAds,
  showGraphs,
  breakdownMode,
  updatingAdsetId,
  updatingAdId,
  onToggle,
  onSelectAd,
  onUpdateStatus,
  onUpdateBudget,
  onDuplicate,
  expandedAds,
  onToggleAdExpansion,
  expandedDates,
  onToggleDateExpansion,
}) => {
  // Check if all ads in campaign are selected
  const allAdsSelected =
    campaign.ads.length > 0 && campaign.ads.every((ad) => selectedAds.has(ad.adId));
  const someAdsSelected = campaign.ads.some((ad) => selectedAds.has(ad.adId)) && !allAdsSelected;

  // Calculate active ads count
  const activeAdsCount = campaign.ads.filter((ad) => ad.status === 'ACTIVE').length;
  const totalAdsCount = campaign.ads.length;

  // Handle select all ads in campaign
  const handleSelectAll = () => {
    campaign.ads.forEach((ad) => {
      onSelectAd(ad.adId);
    });
  };

  return (
    <>
      {/* Campaign Header Row */}
      <TableRow
        sx={{
          bgcolor: '#f0f2f1',
          borderTop: '4px solid transparent',
          borderBottom: '4px solid transparent',
          '&:hover': {
            bgcolor: '#e5e7eb',
          },
        }}
      >
        {/* Expand/Collapse + Checkbox */}
        <TableCell padding="checkbox">
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton size="small" onClick={onToggle}>
              {expanded ? <ExpandIcon fontSize="small" /> : <CollapseIcon fontSize="small" />}
            </IconButton>
            <Checkbox
              checked={allAdsSelected}
              indeterminate={someAdsSelected}
              onChange={handleSelectAll}
              size="small"
            />
          </Box>
        </TableCell>

        {/* Status - Show Active/Total Count */}
        <TableCell>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1.25,
              py: 0.75,
              bgcolor: activeAdsCount === totalAdsCount
                ? '#10b981'
                : activeAdsCount > 0
                ? '#f59e0b'
                : '#9ca3af',
              borderRadius: 2,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
          >
            {/* Status Dot */}
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: 'white',
                boxShadow: '0 0 0 2px rgba(255,255,255,0.3)',
              }}
            />
            {/* Count Ratio */}
            <Typography
              sx={{
                fontSize: '0.95rem',
                fontWeight: 700,
                color: 'white',
                letterSpacing: '-0.01em',
                lineHeight: 1,
              }}
            >
              {activeAdsCount}/{totalAdsCount}
            </Typography>
          </Box>
        </TableCell>

        {/* Budget - Show "Campaign Level" text */}
        <TableCell>
          <Typography variant="body2" color="text.secondary">
            Campaign Level
          </Typography>
        </TableCell>

        {/* Campaign Name */}
        <TableCell sx={{ width: 330, maxWidth: 330 }}>
          <Box>
            <Tooltip title={campaign.campaignName} arrow placement="top">
              <Typography
                variant="body2"
                onClick={onToggle}
                sx={{
                  fontWeight: 600,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  cursor: 'pointer',
                  '&:hover': {
                    color: 'primary.main',
                    textDecoration: 'underline',
                  }
                }}
              >
                {campaign.campaignName}
              </Typography>
            </Tooltip>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
              <Typography variant="caption" color="text.secondary">
                {campaign.ads.length} ad{campaign.ads.length !== 1 ? 's' : ''} •{' '}
                {campaign.adsets.length} adset{campaign.adsets.length !== 1 ? 's' : ''}
              </Typography>
              {campaign.campaignId && (
                <>
                  <Typography variant="caption" color="text.secondary">•</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                      Camp ID:
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 500 }}>
                      {campaign.campaignId}
                    </Typography>
                    <Tooltip title="Copy Campaign ID" arrow>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigator.clipboard.writeText(campaign.campaignId);
                        }}
                        sx={{
                          p: 0.25,
                          '&:hover': { color: 'primary.main' },
                        }}
                      >
                        <ContentCopy sx={{ fontSize: 12 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </>
              )}
            </Box>
          </Box>
        </TableCell>

        {/* Advertiser */}
        <TableCell>
          <Typography variant="body2">{campaign.advertiserName}</Typography>
        </TableCell>

        {/* Aggregated Metrics */}
        <TableCell align="right">
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {formatCurrency(campaign.totalSpend)}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {formatCurrency(campaign.totalRevenue)}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              color: campaign.totalPnl >= 0 ? 'success.main' : 'error.main',
            }}
          >
            {formatCurrency(campaign.totalPnl)}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              color: campaign.avgRoas >= 1.0 ? 'success.main' : 'error.main',
            }}
          >
            {formatPercentage(campaign.avgRoas)}
          </Typography>
        </TableCell>

        {/* Calculated metrics */}
        <TableCell align="right">
          <Typography variant="body2">
            {formatCurrency(calculateRPM(campaign.totalRevenue, campaign.totalSearches))}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2">
            {formatCurrency(calculateRPC(campaign.totalRevenue, campaign.totalClicks))}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2">
            {formatCurrency(calculateCPA(campaign.totalSpend, campaign.totalSearches))}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2">
            {formatCurrency(calculateCPC(campaign.totalSpend, campaign.totalClicks))}
          </Typography>
        </TableCell>
        <TableCell align="right">{formatNumber(campaign.totalClicks)}</TableCell>
        <TableCell align="right">{formatNumber(campaign.totalSearches)}</TableCell>
        <TableCell align="right">
          <Typography variant="body2">
            {formatPercentage(calculatePageCTR(campaign.totalClicks, campaign.totalSearches) / 100)}
          </Typography>
        </TableCell>
        <TableCell align="center">
          <Typography variant="caption" color="text.secondary">
            {getEarliestDate(campaign.ads.map((ad) => ad.adCreatedTime))}
          </Typography>
        </TableCell>
        <TableCell align="center">
          <Typography variant="caption" color="text.secondary">
            {getLatestDate(campaign.ads.map((ad) => ad.adUpdatedTime))}
          </Typography>
        </TableCell>

        {/* Trend Chart */}
        {showGraphs && (
          <TableCell align="center">
            <TrendChart
              data={(() => {
                // Aggregate daily data from all ads
                const dailyData: { [date: string]: { spend: number; revenue: number } } = {};
                campaign.ads.forEach((ad) => {
                  ad.dailyBreakdown?.forEach((day) => {
                    if (!dailyData[day.date]) {
                      dailyData[day.date] = { spend: 0, revenue: 0 };
                    }
                    dailyData[day.date].spend += day.spend;
                    dailyData[day.date].revenue += day.revenue;
                  });
                });
                return Object.entries(dailyData)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([date, data]) => {
                    const roas = calculateROAS(data.revenue, data.spend);
                    return {
                      date,
                      value: roas,
                      label: `${formatDate(date)}: ${formatPercentage(roas)}`,
                    };
                  });
              })()}
              height={32}
              barWidth={4}
              barGap={1}
              colorThresholds={[
                { value: 1.25, color: '#10b981' },
                { value: 1.05, color: '#22c55e' },
                { value: 0.8, color: '#f59e0b' },
                { value: 0, color: '#ef4444' },
              ]}
              valueFormatter={(v) => formatPercentage(v)}
            />
          </TableCell>
        )}

        {/* Coverage */}
        <TableCell align="right">
          <Typography variant="body2">
            {(() => {
              // Collect all daily breakdowns from all ads
              const allDailyBreakdowns = campaign.ads.flatMap((ad) => ad.dailyBreakdown || []);
              const coverage = calculateCoverage(allDailyBreakdowns);
              return formatPercentage(coverage / 100);
            })()}
          </Typography>
        </TableCell>
      </TableRow>

      {/* Expanded: Show ads grouped by adset */}
      {expanded &&
        campaign.adsets.map((adset) => (
          <React.Fragment key={adset.adsetId}>
            {/* AdSet Separator - Show for all adsets when there are multiple */}
            {campaign.adsets.length > 1 && (
              <AdSetSeparator
                adsetName={adset.adsetName}
                adCount={adset.ads.length}
                colSpan={showGraphs ? 21 : 20}
              />
            )}

            {/* Ads in this adset */}
            {adset.ads.map((ad) => (
              <AdRow
                key={ad.adId}
                ad={ad}
                selected={selectedAds.has(ad.adId)}
                showGraphs={showGraphs}
                breakdownMode={breakdownMode}
                updatingAdsetId={updatingAdsetId}
                updatingAdId={updatingAdId}
                onSelect={() => onSelectAd(ad.adId)}
                onUpdateStatus={onUpdateStatus}
                onUpdateBudget={onUpdateBudget}
                onDuplicate={onDuplicate}
                expandedAds={expandedAds}
                onToggleAdExpansion={onToggleAdExpansion}
                expandedDates={expandedDates}
                onToggleDateExpansion={onToggleDateExpansion}
              />
            ))}
          </React.Fragment>
        ))}
    </>
  );
};

export default CampaignRow;
